import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import PublicVenueFacts from "./PublicVenueFacts";

import { fetchVenueDetail, type GraphVenue, type VenueDetail } from "@/api/discover";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { VenueAppearances } from "./VenueEvidenceSheet";
import { formatContactedRecency, formatLocation, formatMonth, latestAppearanceByArtist } from "./discoverCopy";

type Props = {
  artistId: string;
  venue: GraphVenue | null;
  onClose: () => void;
  /** Save / Hide / Add to Booking / Pitch controls, owned by the venues panel. */
  actions?: ReactNode;
};

/**
 * Discover venue detail: everything already known about a venue, before (or after)
 * it becomes a Booking record. Opening it is read-only — it never creates Booking,
 * Saved or contact state, and it never calls an external provider.
 */
export default function VenueDetailSheet({ artistId, venue, onClose, actions }: Props) {
  const globalVenueId = venue?.global_venue_id ?? null;
  const [detail, setDetail] = useState<VenueDetail | null>(null);
  const [detailError, setDetailError] = useState(false);

  useEffect(() => {
    setDetail(null);
    setDetailError(false);
    if (!globalVenueId) return;
    let cancelled = false;
    fetchVenueDetail(globalVenueId, artistId)
      .then((payload) => !cancelled && setDetail(payload))
      .catch(() => !cancelled && setDetailError(true));
    return () => {
      cancelled = true;
    };
  }, [globalVenueId, artistId]);

  const state = venue?.private_state;
  const facts = detail?.facts ?? [];
  const capacity = venue?.capacity ?? detail?.capacity ?? null;
  const latest = latestAppearanceByArtist(venue);

  const bookingLine = (() => {
    const status = (state?.status ?? "").toLowerCase();
    if (!state || (status !== "prospect" && status !== "contacted")) return null;
    if (status === "contacted") {
      return state.contacted_at ? formatContactedRecency(state.contacted_at) : "Contacted";
    }
    return "Prospecting · not contacted yet";
  })();

  return (
    <Sheet open={Boolean(venue)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="max-h-[90dvh] w-full overflow-y-auto sm:max-w-lg">
        {venue && (
          <>
            <SheetHeader>
              <SheetTitle>{venue.name}</SheetTitle>
              <SheetDescription>
                {formatLocation([venue.city, venue.region, venue.country]) || "Location unknown"}
                {capacity ? ` · ${capacity.toLocaleString()} capacity` : ""}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-5 text-sm">
              {venue.identity?.status === "needs_review" && (
                <p className="rounded-lg bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                  <span className="font-semibold">Needs review</span> ·{" "}
                  {venue.identity.reason ?? "This venue's identity needs review."} It can't be saved or added to Booking yet.
                </p>
              )}

              {globalVenueId && <Link to={`/discover/venues/${globalVenueId}`} className="text-xs text-primary hover:underline">Open venue details</Link>}

              {actions && (
                <div className="flex flex-wrap items-center gap-2" aria-label="Venue actions">
                  {actions}
                </div>
              )}

              {bookingLine && <p>In Booking · {bookingLine}{state?.booking_lead_id && <> · <Link to={`/contacts/leads/${state.booking_lead_id}`} className="text-primary">Open in Booking</Link></>}</p>}
              <section aria-labelledby="venue-detail-why">
                <h3 id="venue-detail-why" className="mb-1 font-semibold text-foreground">
                  Why it fits
                </h3>
                {venue.in_comparable_graph === false ? (
                  <p className="text-muted-foreground">
                    You saved this venue earlier. None of your current comparable artists have played here.
                  </p>
                ) : (
                  <>
                    <p className="font-medium text-foreground">
                      Played by {venue.comparable_artist_count} of your comparable artist
                      {venue.comparable_artist_count === 1 ? "" : "s"}
                    </p>
                    <ul className="text-muted-foreground" aria-label={`Comparable artists who played ${venue.name}`}>
                      {latest.map((event) => (
                        <li key={event.public_artist_id}>
                          <span className="text-foreground/90">{event.artist_name}</span> · {formatMonth(event.event_date)}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>

              {!detail && !detailError && globalVenueId && <p>Loading venue details…</p>}
              {detailError && <p>Venue details couldn't be loaded right now.</p>}
              <PublicVenueFacts facts={facts} />

              {venue.events.length > 0 && (
                <section aria-labelledby="venue-detail-appearances" className="space-y-2">
                  <h3 id="venue-detail-appearances" className="font-semibold text-foreground">
                    Appearances
                  </h3>
                  <VenueAppearances venue={venue} />
                </section>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
